import type { SessionSummary } from '@/entities/sender.entity';
import type { SenderAccountMode, SenderPhoneLoginStep } from '@/pages/sender/launch-types';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { SENDER_TWO_FACTOR_MASS_SEND_MESSAGE } from '@/lib/format-sender-api-error';
import { cn } from '@/lib/utils';
import { FileIcon, PhoneIcon, UserIcon } from 'lucide-react';

interface ModeCardProps {
	active: boolean;
	title: string;
	description: string;
	icon: typeof PhoneIcon;
	onClick: () => void;
}

function ModeCard({ active, title, description, icon: Icon, onClick }: ModeCardProps) {
	return (
		<button
			type='button'
			onClick={onClick}
			className={cn(
				'rounded-xl border px-3 py-2.5 text-left transition-colors',
				active ? 'border-chart-4/50 bg-chart-4/10' : 'border-border bg-card active:bg-accent/30',
			)}
		>
			<div className='flex items-start gap-2.5'>
				<div className='rounded-lg bg-chart-4/15 p-1.5 shrink-0'>
					<Icon className='text-chart-4' size={18} />
				</div>
				<div className='min-w-0'>
					<p className='text-sm font-medium'>{title}</p>
					<p className='text-xs text-muted-foreground'>{description}</p>
				</div>
			</div>
		</button>
	);
}

export interface SenderAccountStepProps {
	showModePicker?: boolean;
	inlinePhoneActions?: boolean;
	sessions: SessionSummary[];
	sessionsLoading: boolean;
	requireSidecar: boolean;
	accountMode: SenderAccountMode;
	onAccountModeChange: (mode: SenderAccountMode) => void;
	sessionId: string;
	onSessionIdChange: (id: string) => void;
	sessionFile: File | null;
	sessionFiles: File[];
	onSessionFileChange: (file: File | null) => void;
	onSessionFilesChange: (files: File[]) => void;
	sessionSidecar: File | null;
	onSessionSidecarChange: (file: File | null) => void;
	sessionLabel: string;
	onSessionLabelChange: (label: string) => void;
	phoneLoginStep: SenderPhoneLoginStep;
	phone: string;
	onPhoneChange: (v: string) => void;
	phoneLabel: string;
	onPhoneLabelChange: (v: string) => void;
	code: string;
	onCodeChange: (v: string) => void;
	codeType: string;
	onRequestCode: () => void;
	onConfirmCode: () => void;
	phoneLoginSaving: boolean;
	autoSpamAfterPhoneLogin: boolean;
	onAutoSpamAfterPhoneLoginChange: (checked: boolean) => void;
}

export default function SenderAccountStep({
	showModePicker = true,
	inlinePhoneActions = true,
	sessions,
	sessionsLoading,
	requireSidecar,
	accountMode,
	onAccountModeChange,
	sessionId,
	onSessionIdChange,
	sessionFile,
	sessionFiles,
	onSessionFileChange,
	onSessionFilesChange,
	sessionSidecar,
	onSessionSidecarChange,
	sessionLabel,
	onSessionLabelChange,
	phoneLoginStep,
	phone,
	onPhoneChange,
	phoneLabel,
	onPhoneLabelChange,
	code,
	onCodeChange,
	codeType,
	onRequestCode,
	onConfirmCode,
	phoneLoginSaving,
	autoSpamAfterPhoneLogin,
	onAutoSpamAfterPhoneLoginChange,
}: SenderAccountStepProps) {
	const codeHint =
		codeType === 'app'
			? 'Код придёт в приложение Telegram на этом номере'
			: 'Код придёт в SMS на указанный номер';

	const showRegistry = sessions.length > 0;

	return (
		<div className='flex flex-col gap-4'>
			{showModePicker ? (
			<div className={cn('grid gap-2', showRegistry ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2')}>
				{showRegistry ? (
					<ModeCard
						active={accountMode === 'registry'}
						title='Готовые'
						description={`${sessions.length} в списке`}
						icon={UserIcon}
						onClick={() => onAccountModeChange('registry')}
					/>
				) : null}
				<ModeCard
					active={accountMode === 'phone'}
					title='Вход по номеру'
					description='SMS или код в Telegram'
					icon={PhoneIcon}
					onClick={() => onAccountModeChange('phone')}
				/>
				<ModeCard
					active={accountMode === 'file'}
					title='Файл .session'
					description='Загрузка с диска'
					icon={FileIcon}
					onClick={() => onAccountModeChange('file')}
				/>
			</div>
			) : null}

			{sessionsLoading ? <p className='text-sm text-muted-foreground'>Загрузка сессий…</p> : null}

			{accountMode === 'registry' && showRegistry ? (
				<div className='flex flex-col gap-2'>
					<Label>Выберите сессию</Label>
					<div className='flex flex-col gap-2'>
						{sessions.map(session => {
							const selected = sessionId === session.id;
							const title = session.label || session.username || session.phoneMasked;
							return (
								<button
									key={session.id}
									type='button'
									onClick={() => onSessionIdChange(session.id)}
									className={cn(
										'rounded-xl border px-3 py-2.5 text-left text-sm',
										selected ? 'border-chart-4/50 bg-chart-4/10' : 'border-border bg-card',
									)}
								>
									<p className='font-medium truncate'>{title}</p>
									<p className='text-xs text-muted-foreground truncate'>{session.phoneMasked}</p>
								</button>
							);
						})}
					</div>
				</div>
			) : null}

			{accountMode === 'phone' ? (
				<div className='flex flex-col gap-3'>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='launch-session-phone'>Номер телефона</Label>
						<InputGroup>
							<InputGroupInput
								id='launch-session-phone'
								type='tel'
								placeholder='+998901234567'
								value={phone}
								onChange={e => onPhoneChange(e.target.value)}
								disabled={phoneLoginStep === 'code'}
							/>
						</InputGroup>
					</div>
					{phoneLoginStep === 'phone' ? (
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='launch-session-label'>Метка (опционально)</Label>
							<InputGroup>
								<InputGroupInput
									id='launch-session-label'
									placeholder='account_01'
									value={phoneLabel}
									onChange={e => onPhoneLabelChange(e.target.value)}
								/>
							</InputGroup>
						</div>
					) : (
						<>
							<p className='text-[13px] text-muted-foreground'>{codeHint}</p>
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='launch-session-code'>Код</Label>
								<InputGroup>
									<InputGroupInput
										id='launch-session-code'
										inputMode='numeric'
										autoComplete='one-time-code'
										placeholder='12345'
										value={code}
										onChange={e => onCodeChange(e.target.value)}
									/>
								</InputGroup>
							</div>
							<p className='text-[13px] text-muted-foreground'>{SENDER_TWO_FACTOR_MASS_SEND_MESSAGE}</p>
							<label className='flex min-h-11 items-start gap-2 text-[15px]'>
								<input
									type='checkbox'
									className='mt-1'
									checked={autoSpamAfterPhoneLogin}
									onChange={e => onAutoSpamAfterPhoneLoginChange(e.target.checked)}
								/>
								<span>Запустить сразу после настройки</span>
							</label>
						</>
					)}
					{inlinePhoneActions ? (
						<Button
							type='button'
							className='min-h-11'
							disabled={phoneLoginSaving}
							onClick={() => void (phoneLoginStep === 'phone' ? onRequestCode() : onConfirmCode())}
						>
							{phoneLoginStep === 'phone' ? 'Запросить код' : 'Подтвердить'}
						</Button>
					) : null}
				</div>
			) : null}

			{accountMode === 'file' ? (
				<div className='flex flex-col gap-3'>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='launch-session-file'>Файл .session</Label>
						<InputGroup>
							<InputGroupInput
								id='launch-session-file'
								type='file'
								accept='.session'
								multiple
								onChange={e => {
									const list = e.target.files ? [...e.target.files] : [];
									onSessionFilesChange(list);
									onSessionFileChange(list[0] ?? null);
								}}
							/>
						</InputGroup>
						{sessionFiles.length > 0 ? (
							<p className='text-xs text-muted-foreground'>
								Выбрано файлов: {sessionFiles.length}
								{sessionFile ? ` (мастер: ${sessionFile.name})` : ''}
							</p>
						) : null}
					</div>
					{requireSidecar ? (
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='launch-session-sidecar'>JSON сессии</Label>
							<InputGroup>
								<InputGroupInput
									id='launch-session-sidecar'
									type='file'
									accept='.json,application/json'
									onChange={e => onSessionSidecarChange(e.target.files?.[0] ?? null)}
								/>
							</InputGroup>
							{sessionSidecar ? (
								<p className='text-xs text-muted-foreground'>JSON сессии: {sessionSidecar.name}</p>
							) : null}
						</div>
					) : null}
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='launch-file-label'>Метка сессии (опционально)</Label>
						<InputGroup>
							<InputGroupInput
								id='launch-file-label'
								placeholder='account_01'
								value={sessionLabel}
								onChange={e => onSessionLabelChange(e.target.value)}
							/>
						</InputGroup>
					</div>
				</div>
			) : null}
		</div>
	);
}
